import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import Breadcrumbs from './Breadcrumbs'
import { useCatalog } from '../context/CatalogContext'
import { getDocsIndex, searchDocs, DocSearchRecord } from '../utils/docsSearch'

type SearchItem = { title: string; to: string; group: string; external?: boolean }
type Result = { title: string; group: string; to: string; external?: boolean }

/**
 * Sticky docs sub-header: breadcrumb on the left, a prominent search in the middle
 * (with a `/` focus shortcut and a live title dropdown built from the catalog), and a version pill on
 * the right. Sticks directly below the main site header.
 */
export default function DocsSubHeader() {
  const { catalog } = useCatalog()
  const navigate = useNavigate()
  const [q, setQ] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const [active, setActive] = React.useState(0)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const items = React.useMemo<SearchItem[]>(() => {
    const out: SearchItem[] = []
    catalog?.docs.forEach((d) =>
      out.push({ title: d.title, to: d.section === 'external' ? d.path : `/docs/${d.slug}`, group: 'Docs', external: d.section === 'external' }),
    )
    catalog?.apis.forEach((a) => out.push({ title: a.title, to: `/docs/api-docs/${a.id}`, group: 'API' }))
    out.push({ title: 'Software & Players', to: '/software', group: 'Software' })
    out.push({ title: 'Support', to: '/support', group: 'Support' })
    return out
  }, [catalog])

  const [docIndex, setDocIndex] = React.useState<DocSearchRecord[]>([])
  React.useEffect(() => {
    let cancelled = false
    getDocsIndex().then((idx) => { if (!cancelled) setDocIndex(idx) })
    return () => { cancelled = true }
  }, [])

  const results = React.useMemo<Result[]>(() => {
    const query = q.trim()
    if (!query) return []
    // Full-text results from the docs content (page titles + section headings + body).
    const content: Result[] = searchDocs(docIndex, query, 6).map((r) => ({
      title: r.heading || r.pageTitle,
      group: r.heading ? r.pageTitle : 'Docs',
      to: r.url + r.anchor,
    }))
    const seen = new Set(content.map((c) => c.to.split('#')[0]))
    // Catalog title matches (APIs, software, external) not already covered.
    const titleHits: Result[] = items
      .filter((i) => i.title.toLowerCase().includes(query.toLowerCase()) && !seen.has(i.to))
      .map((i) => ({ title: i.title, group: i.group, to: i.to, external: i.external }))
    return [...content, ...titleHits].slice(0, 8)
  }, [q, docIndex, items])

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName || '').toLowerCase()
      if (e.key === '/' && tag !== 'input' && tag !== 'textarea') {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const go = (item: Result) => {
    setQ('')
    setOpen(false)
    if (item.external) window.open(item.to, '_blank')
    else navigate(item.to)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!results.length) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % results.length) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length) }
    else if (e.key === 'Enter') { e.preventDefault(); go(results[active] ?? results[0]) }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur() }
  }

  return (
    <div className="docs-subheader">
      <div className="docs-subheader-inner">
        <div className="dsh-crumbs">
          <Breadcrumbs />
        </div>

        <div className="dsh-search">
          <Search size={15} className="dsh-search-icon" />
          <input
            ref={inputRef}
            value={q}
            placeholder="Search documentation"
            onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0) }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={onKeyDown}
          />
          <kbd className="dsh-kbd">/</kbd>
          {open && results.length > 0 && (
            <div className="dsh-results">
              {results.map((r, i) => (
                <button
                  key={`${r.to}-${i}`}
                  type="button"
                  className={`dsh-result ${i === active ? 'is-active' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => { e.preventDefault(); go(r) }}
                >
                  <span className="dsh-result-title">{r.title}</span>
                  <span className="dsh-result-group">{r.group}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="dsh-version" title="Documentation version">
          <span>v{__APP_VERSION__}</span>
        </div>
      </div>
    </div>
  )
}
